import { TextField, type TextFieldProps } from "@mui/material";
import type { ReactNode } from "react";
import {
    type Control,
    type FieldPathByValue,
    type FieldValues,
    useController,
} from "react-hook-form";
import { defaultCountries, usePhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod/mini";

type Props<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPathByValue<TFieldValues, string> = FieldPathByValue<TFieldValues, string>,
> = Omit<TextFieldProps, "error" | "onChange" | "onBlur" | "value" | "inputRef"> & {
    control: Control<TFieldValues>;
    name: TName;
};

export const RhfPhoneField = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPathByValue<TFieldValues, string> = FieldPathByValue<TFieldValues, string>,
>({
    control,
    name,
    ...rest
}: Props<TFieldValues, TName>): ReactNode => {
    const { field, fieldState } = useController({
        control,
        name,
    });

    const { inputValue, handlePhoneValueChange, inputRef } = usePhoneInput({
        defaultCountry: "us",
        value: field.value,
        countries: defaultCountries,
        onChange: (data) => {
            field.onChange(data.phone);
        },
    });

    return (
        <TextField
            {...rest}
            name={name}
            error={Boolean(fieldState.error)}
            onChange={handlePhoneValueChange}
            onBlur={field.onBlur}
            value={inputValue}
            inputRef={inputRef}
            helperText={fieldState.error?.message ?? rest.helperText}
        />
    );
};

export const phoneNumberSchema = z
    .string()
    .check(z.refine(isValidPhoneNumber, "Invalid phone number"));
